import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Calendar, Infinity as InfinityIcon, ArrowRight, Clock,
  Package, Truck, Flag, Globe,
} from "lucide-react";
import { getActivePromo, getMonthEnd } from "@/data/promoCalendar";

/**
 * Sticky promo banner — driven by promoCalendar.js (one offer per calendar month).
 * Auto-hides on months without a promo entry.
 *
 * - Desktop: right-rail fixed widget.
 * - Mobile: collapsed gold pill bottom-right, expands on tap.
 * - Dismiss (X) → hidden for the session via sessionStorage.
 * - Click "Jetzt buchen" → deep-links into the wizard with the promo pre-applied.
 * - Countdown ticks to end of current month (creates monthly urgency).
 */

const ICONS = {
  calendar: Calendar,
  infinity: InfinityIcon,
  flag: Flag,
  globe: Globe,
  clock: Clock,
  package: Package,
  truck: Truck,
};

const useCountdown = (target) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 60);
    return () => clearInterval(id);
  }, []);
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return { expired: true, label: "Abgelaufen" };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const label =
    days > 1 ? `Noch ${days} Tage` : days === 1 ? `Noch 1 Tag · ${hours}h` : `Nur noch ${hours}h`;
  return { expired: false, days, hours, label };
};

const PromoBanner = () => {
  const navigate = useNavigate();
  const promo = getActivePromo();
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const deadline = getMonthEnd();
  const countdown = useCountdown(deadline);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Dismiss key includes the promo code so a new month re-enables the banner
    const key = `nivara_promo_${promo?.code || "none"}_dismissed_v1`;
    if (sessionStorage.getItem(key) === "1") setOpen(false);
  }, [promo]);

  if (!promo || !open || countdown.expired) return null;

  const dismiss = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(`nivara_promo_${promo.code}_dismissed_v1`, "1");
    } catch (e) { /* ignore */ }
  };

  const book = () => {
    // Fire-and-forget tracking call
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/promo-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promo.code, source: expanded ? "banner_mobile" : "banner" }),
        keepalive: true,
      });
    } catch (e) { /* ignore */ }
    navigate(promo.deepLink);
    if (window.location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const deadlineDate = deadline.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // Split the title at \n so we can render multi-line headlines elegantly
  const titleLines = promo.title.split("\n");

  return (
    <>
      {/* Desktop: fixed right rail */}
      <aside
        className="hidden xl:block fixed top-1/2 right-4 -translate-y-1/2 z-40 w-[260px]"
        data-testid="promo-banner-desktop"
      >
        <div className="relative bg-white border-2 border-black shadow-2xl overflow-hidden">
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center bg-black/10 hover:bg-black hover:text-white transition-colors"
            aria-label="Aktion schliessen"
            data-testid="promo-banner-close"
          >
            <X size={14} />
          </button>

          <div className="bg-[#D4AF37] text-black text-center py-2">
            <div className="font-[Chivo] text-[10px] font-black uppercase tracking-[0.4em]">★ Aktion ★</div>
          </div>

          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--rs-text-muted)]">{promo.vehicle}</div>
            <div className="font-[Chivo] text-[26px] font-black leading-[0.95] mt-1">
              {titleLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
              <span className="text-[#D4AF37]">{promo.priceChf.toLocaleString("de-CH")}.–</span>
            </div>
            {promo.fromChf && (
              <div className="text-xs mt-1 text-[var(--rs-text-muted)]">
                statt <span className="line-through">CHF {promo.fromChf.toLocaleString("de-CH")}.–</span>
              </div>
            )}

            {/* Countdown */}
            <div className="mt-3 bg-black text-white px-3 py-2 flex items-center gap-2" data-testid="promo-countdown">
              <Clock size={13} className="text-[#D4AF37] flex-shrink-0" />
              <div className="text-[11px] leading-tight">
                <div className="font-bold uppercase tracking-wider">{countdown.label}</div>
                <div className="text-white/60 text-[10px]">bis {deadlineDate}</div>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-xs">
              {promo.bullets.map((b, i) => {
                const I = ICONS[b.icon] || Calendar;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <I size={14} className="text-[#D4AF37] flex-shrink-0" /> {b.text}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={book}
              className="mt-5 w-full bg-black text-white hover:bg-[var(--rs-primary)] py-3 text-[11px] font-bold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="promo-banner-book"
            >
              Jetzt buchen <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile / tablet */}
      <div className="xl:hidden fixed bottom-20 right-3 z-40" data-testid="promo-banner-mobile">
        {expanded ? (
          <div className="bg-white border-2 border-black shadow-2xl w-[260px]">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/10 hover:bg-black hover:text-white"
              data-testid="promo-banner-collapse"
            >
              <X size={14} />
            </button>
            <div className="bg-[#D4AF37] text-black text-center py-1.5 text-[10px] font-black uppercase tracking-[0.3em]">
              ★ Aktion ★
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--rs-text-muted)]">{promo.vehicle}</div>
              <div className="font-[Chivo] text-2xl font-black mt-1">
                {titleLines.join(" · ")} <span className="text-[#D4AF37]">{promo.priceChf.toLocaleString("de-CH")}.–</span>
              </div>
              {promo.fromChf && (
                <div className="text-[11px] mt-1 text-[var(--rs-text-muted)]">
                  statt <span className="line-through">CHF {promo.fromChf.toLocaleString("de-CH")}.–</span>
                </div>
              )}
              <div className="mt-3 bg-black text-white text-[11px] px-2 py-1.5 flex items-center gap-1.5">
                <Clock size={12} className="text-[#D4AF37]" />
                <span className="font-bold">{countdown.label}</span>
                <span className="text-white/50 text-[9px] ml-auto">{deadlineDate}</span>
              </div>
              <button
                type="button"
                onClick={book}
                className="mt-4 w-full bg-black text-white py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2"
                data-testid="promo-banner-book-mobile"
              >
                Jetzt buchen <ArrowRight size={14} />
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="mt-2 w-full text-[10px] uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-black"
              >
                Aktion ausblenden
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="bg-[#D4AF37] text-black px-4 py-3 shadow-2xl font-[Chivo] font-black text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
            data-testid="promo-banner-expand"
            aria-label="Aktion öffnen"
          >
            ★ Aktion · CHF {promo.priceChf}.–
          </button>
        )}
      </div>
    </>
  );
};

export default PromoBanner;
